import { DocumentService } from './documentService'
import { ETipoAsignatura, ETipoError, ICertificate, ICertificateSearchResult, NOMBRE_CARGO, Plane, TSearchType } from '@shared/types'
import { apiService } from './apiService'
import { database } from '@/firebase/config'
import { ref as dbRef, get, query, orderByChild, equalTo, update } from 'firebase/database'
import JSZip from 'jszip'
import { getAuth } from 'firebase/auth'
import { completeZeros, fixPressisionError, truncate } from '@shared/modules/FunctionUtils'
const errorMessages: string[] = [
	'El certificado no podrá empaquetarse porque fue expedido hace más de 90 días. La SEP solo permite autenticar certificados cuya fecha de expedición sea de hasta 90 días antes de la fecha en que se cargan al MEC.'
]
export class CertificateService extends DocumentService {
	protected institution: string

	constructor() {
		super('/api/certificates')
		this.institution = '' // Se debe inicializar con el valor correcto
	}

	async getCertificates(): Promise<ICertificate[]> {
		const documents = await this.getDocuments()
		return documents as ICertificate[]
	}

	async validateCertificate(certificateId: string): Promise<ICertificate> {
		const document = await this.validateDocument(certificateId)
		return document as ICertificate
	}

	async searchByProgram(program: string): Promise<ICertificate[]> {
		try {
			const response = await fetch(`${this.basePath}/search/program/${encodeURIComponent(program)}`)
			if (!response.ok) throw new Error('Error al buscar certificados por programa')
			return await response.json()
		} catch (error) {
			console.error('Error en searchByProgram:', error)
			throw error
		}
	}

	async downloadTemplate(idCarrera: string, revoe: string, plan: string, institutionId: string, name?: string): Promise<Blob> {
		const response = await apiService.post(
			'downloadTemplate',
			{
				institution: institutionId,
				type: 'certificate',
				data: {
					carrera: idCarrera,
					revoe: revoe,
					plan: plan
				}
			},
			{
				responseType: 'arraybuffer'
			}
		)

		// Crear y descargar el archivo
		const filename = `Cer-${name ?? idCarrera + '_' + revoe + '_' + plan}.xlsx`.replace(/\s/gi, '')
		const blob = new Blob([response], {
			type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
		})

		// Crear un enlace temporal para la descarga
		const link = document.createElement('a')
		link.href = window.URL.createObjectURL(blob)
		link.download = filename
		document.body.appendChild(link)
		link.click()
		document.body.removeChild(link)
		window.URL.revokeObjectURL(link.href)

		return blob
	}

	async searchCertificates(type: TSearchType, value: string, institutionId: string, testMode: boolean = false): Promise<ICertificateSearchResult[]> {
		const path = `/institutions/${institutionId}${testMode ? '/testMode' : ''}/certificates/queue`
		const db = database
		const degreesRef = dbRef(db, path)

		let searchQuery
		if (type === 'folio') {
			searchQuery = query(degreesRef, orderByChild('folioControl'), equalTo(value))
		} else if (type === 'numero') {
			searchQuery = query(degreesRef, orderByChild('data/numeroControl'), equalTo(value))
		} else if (type === 'curp') {
			searchQuery = query(degreesRef, orderByChild('data/Alumno/curp'), equalTo(value))
		} else {
			throw new Error('Tipo de búsqueda no permitido')
		}

		const snapshot = await get(searchQuery)
		if (!snapshot.exists()) return []

		const results: ICertificate[] = Object.entries(snapshot.val()).map(([id, data]: [string, any]) => ({
			id,
			...data
		}))

		const packageIds = [...new Set(results.map(cert => cert.package).filter((id): id is number => Boolean(id)))]
		const packageLabels = new Map<number, string>()

		await Promise.all(
			packageIds.map(async packageId => {
				const packageRef = dbRef(db, `/institutions/${institutionId}${testMode ? '/testMode' : ''}/certificates/packages/${packageId}`)
				const packageSnapshot = await get(packageRef)
				if (packageSnapshot.exists() && packageSnapshot.val()?.label) {
					packageLabels.set(packageId, packageSnapshot.val().label)
				}
			})
		)

		return results.map(cert => ({
			...cert,
			packageLabel: cert.package ? packageLabels.get(cert.package) : undefined
		}))
	}

	async getZipOfPackage(params: { package: number; institution: string; testMode?: boolean; name?: string }): Promise<any> {
		try {
			if (!params.institution) {
				throw new Error('Se requiere el ID de la institución')
			}

			const testPath = params.testMode ? '/testMode' : ''
			const path = `/institutions/${params.institution}${testPath}/certificates/queue`
			const db = database
			const degreesRef = dbRef(db, path)

			// Verificar permisos antes de intentar acceder a los datos
			try {
				const snapshot = await get(query(degreesRef, orderByChild('package'), equalTo(params.package)))

				if (!snapshot.exists()) {
					throw new Error('No se encontraron certificados para el paquete especificado')
				}

				const documents = snapshot.val()
				const zip = new JSZip()

				const errores: string[] = []

				Object.values(documents).forEach((document: any) => {
					if (!document.folioControl || !document.cadenaOriginal || !document.xml) {
						const message = errorMessages.some(error => document.error.message.includes(error)) ? 'El certificado no puede generarse porque fue expedido hace más de 90 días.' : document.error.message
						errores.push(message)
					}
					if (errores.length > 0) {
						throw new Error(errores.join('\n'))
					}

					const name = document.folioControl
					zip.file('COs/' + name + '.txt', document.cadenaOriginal)
					zip.file('XMLs/' + name + '.xml', document.xml)

					const parser = new DOMParser()
					const xmlDoc = parser.parseFromString(document.xml, 'text/xml')
					const sello = xmlDoc.getElementsByTagName('Dec')[0]?.getAttribute('sello')

					if (!sello) {
						console.warn(`Documento ${name} no tiene sello`)
					}
				})

				const b64Str = await zip.generateAsync({ type: 'base64' })
				const link = document.createElement('a')
				const fileName = (params.name ?? documents.label ?? 'paquete').replace(/\s/gi, '') + new Date().toISOString().replace(/[-:]/g, '').slice(0, 8) + '.zip'

				link.download = fileName
				link.href = 'data:application/zip;base64,' + b64Str
				link.click()

				return { success: true, fileName }
			} catch (dbError: any) {
				if (dbError.code === ETipoError.PERMISSION_DENIED) {
					throw new Error('No tienes permisos para acceder a estos certificados. Por favor, verifica tus credenciales.')
				}
				throw dbError
			}
		} catch (error: any) {
			console.error('Error en getZipOfPackage:', error)
			throw new Error(error.message || 'Error al generar el archivo ZIP')
		}
	}

	async saveData(certificate: ICertificate, plan: Plane, institution: number, testMode: boolean = false, isEdit: boolean = false): Promise<void> {
		try {
			if (!isEdit && certificate.id === '') {
				certificate.id = new Date().getTime().toString()
			}
			// Verificar permisos
			const auth = getAuth()
			const user = auth.currentUser
			if (!user) {
				throw new Error('Usuario no autenticado')
			}

			// Verificar acceso a la institución
			const userInstitutionRef = dbRef(database, `/users/${user.uid}/institutions/${institution}`)
			const userInstitutionSnapshot = await get(userInstitutionRef)
			if (!userInstitutionSnapshot.exists() || !userInstitutionSnapshot.val()) {
				throw new Error('No tienes permisos para esta institución')
			}

			const asignaturas = certificate.data.Asignaturas.Asignatura
			const testPath = testMode ? '/testMode' : ''
			const path = `/institutions/${institution}${testPath}/certificates/queue/${certificate.id}`
			const db = database

			// Verificar si el documento ya tiene package
			const certificateRef = dbRef(db, path)
			const snapshot = await get(certificateRef)
			if (snapshot.exists() && snapshot.val().package) {
				throw new Error('No se puede modificar un certificado que ya está en un paquete')
			}

			// Procesar asignaturas y calcular créditos
			let creditosObtenidos = 0
			let sumaCalificaciones = 0
			const cycles: string[] = []
			const decimalsForCredits = 2 // Valor por defecto
			const asignaturasObligatorias = asignaturas.filter(a => a.idTipoAsignatura !== ETipoAsignatura.COMPLEMENTARIA)
			const esEquivalencia = (idObservaciones: number | string) => Number(idObservaciones) === 70
			const asignaturasOrdinarias = asignaturasObligatorias.filter(a => !esEquivalencia(a.idObservaciones))
			const minimaAprobatoria = Number(certificate.data.Carrera.calificacionMinimaAprobatoria)
			const sumarCreditos = (asignatura: (typeof asignaturas)[number]) => {
				const creditosPlan = Number(plan.Asignaturas[asignatura.idAsignatura].creditos)
				creditosObtenidos = Number(fixPressisionError(creditosObtenidos + creditosPlan, decimalsForCredits))
				asignatura.creditos = completeZeros(creditosPlan, [], decimalsForCredits).toString()
				asignatura.order = plan.Asignaturas[asignatura.idAsignatura].order || 0
				if (cycles.indexOf(asignatura.ciclo) < 0) {
					cycles.push(asignatura.ciclo)
				}
			}
			asignaturasObligatorias.filter(a => esEquivalencia(a.idObservaciones)).forEach(sumarCreditos)
			asignaturasOrdinarias.forEach(asignatura => {
				if (Number(asignatura.calificacion) >= minimaAprobatoria) {
					sumarCreditos(asignatura)
					const calificacion = Number(asignatura.calificacion)
					// No truncar la suma: 112.39999999999999 se cortaría a 112.39 y el promedio bajaría un centésimo.
					sumaCalificaciones = Number(fixPressisionError(sumaCalificaciones + calificacion, decimalsForCredits))
					asignatura.calificacion = completeZeros(Number(calificacion), [10], 2).toString()
				}
			})

			// Ordenar asignaturas por ciclo y clave
			asignaturas.sort((a, b) => {
				const labelSortA = `${a.ciclo}_${a.claveAsignatura}`.toUpperCase()
				const labelSortB = `${b.ciclo}_${b.claveAsignatura}`.toUpperCase()
				return labelSortA < labelSortB ? -1 : 1
			})
			// Actualizar datos calculados
			const promedioInicial = asignaturasOrdinarias.length > 0 ? sumaCalificaciones / asignaturasOrdinarias.length : 0
			// Más de 2 decimales: corrige el error binario (p. ej. 9.075000000000001) sin redondear a 9.08. truncate deja 2 decimales.
			const promedioLimpio = Number.isFinite(promedioInicial) ? fixPressisionError(promedioInicial, 6) : 0
			certificate.data.Asignaturas.promedio = completeZeros(Number(truncate(promedioLimpio, decimalsForCredits)), [10], decimalsForCredits).toString()
			certificate.data.Asignaturas.creditosObtenidos = completeZeros(Number(creditosObtenidos), [], decimalsForCredits).toString()
			certificate.data.Asignaturas.totalCreditos = completeZeros(Number(certificate.data.Asignaturas.totalCreditos), [], decimalsForCredits).toString()
			certificate.data.Asignaturas.numeroCiclos = cycles.length
			certificate.data.FirmaResponsables = {}
			certificate.data.FirmaResponsables[certificate.data.Ipes.Responsable.curp] = {
				curp: certificate.data.Ipes.Responsable.curp,
				nombre: certificate.data.Ipes.Responsable.nombre,
				primerApellido: certificate.data.Ipes.Responsable.primerApellido,
				segundoApellido: certificate.data.Ipes.Responsable.segundoApellido,
				abrTitulo: '',
				cargo: NOMBRE_CARGO[certificate.data.Ipes.Responsable.idCargo],
				certificadoResponsable: '',
				noCertificadoResponsable: '',
				sello: '',
				idCargo: certificate.data.Ipes.Responsable.idCargo
			}
			certificate.data.Alumno.numeroControl = certificate.data.Alumno.numeroControl.toString()
			certificate.data.numeroControl = certificate.data.Alumno.numeroControl.toString()
			const updates: { [key: string]: any } = {
				[`${path}/data`]: certificate.data
			}
			if (isEdit) {
				updates[`${path}/date`] = new Date().getTime()
			}
			await update(dbRef(db), updates)
		} catch (error) {
			alert('Error al guardar el certificado: ' + error)
			throw new Error('Error al guardar el certificado')
		}
	}

	async addSignatures(institutionId: string, face: string, signatureFace: string, deviceId: string, certificates: any, testMode: boolean = false): Promise<void> {
		try {
			await apiService.post('addSignatures', {
				institution: institutionId,
				face,
				signatureFace,
				deviceId,
				certificates,
				testMode
			})
		} catch (error: any) {
			console.error('Error adding signatures:', error)
			throw new Error(error.message || 'Error al agregar las firmas')
		}
	}

	/**
	 * Crea un paquete de certificados
	 * @param certificates Mapa de certificados a incluir en el paquete
	 * @param institutionId ID de la institución
	 * @param name Nombre del paquete
	 * @param testMode Si está en modo de prueba
	 * @returns Promise con la respuesta del servidor
	 */
	async createPackage(certificates: Record<string, boolean>, institutionId: string, name: string, testMode: boolean = false): Promise<any> {
		// Validar que los certificados existan y estén validados
		const db = database
		const testPath = testMode ? '/testMode' : ''
		const path = `/institutions/${institutionId}${testPath}/certificates/queue`

		// Verificar cada certificado
		for (const certificateId of Object.keys(certificates)) {
			const certificateRef = dbRef(db, `${path}/${certificateId}`)
			const snapshot = await get(certificateRef)

			if (!snapshot.exists()) {
				throw new Error(`El certificado ${certificateId} no existe`)
			}

			const certificateData = snapshot.val()
			console.log('certificateData', certificateData)
			if (!certificateData.success) {
				throw new Error(`El certificado ${certificateId} no ha sido validado correctamente`)
			}
		}

		return apiService.post('createPackage', {
			certificates,
			institution: institutionId,
			name,
			testMode
		})
	}

	async deletePackage(packageId: number, institutionId: string, testMode: boolean = false): Promise<any> {
		return apiService.post('deletePackage', {
			institution: institutionId,
			package: packageId,
			certificates: true,
			testMode
		})
	}
}
export const certificateService = new CertificateService()
